import React, {useEffect, useRef, useState} from 'react'
import {Link, Link as RouterLink} from 'react-router-dom'
import {
  DayItem,
  DayItems,
  DayItemText,
  Divider,
  HeaderTitle,
  ObjectiveText,
  ObjectiveTitle,
  SidenavBox,
  SidenavExpandedContent,
  SidenavToggleBox,
  StyledIcon,
} from '../AssetsSidenav/styles'

import SidenavHideIcon from '../../../images/icons/new-dashboard/sidenav-hide-icon.svg'
import SidenavExpandIcon from '../../../images/icons/new-dashboard/sidenav-expand-icon.svg'
import VideoIcon from '../../../images/icons/new-dashboard/video-icon.svg'
import VideoIconActive from '../../../images/icons/new-dashboard/live-video.svg'
import QuizIcon from '../../../images/icons/new-dashboard/quiz-icon.svg'
import QuizIconActive from '../../../images/icons/new-dashboard/quiz-active-icon.svg'
import CompleteIcon from '../../../images/icons/new-dashboard/complete-icon.svg'
import {LinkBox} from '../../../styles/global'
import {Course} from '../../../redux/courses'
import {VideoGroup} from '../../../redux/videos'
import {useSelector} from 'react-redux'
import {selectMemberAccess} from '../../../redux/members'
import pluralize from 'pluralize'
import {Lesson} from '../../../redux/lessons'

type Props = {
  id: string
  course: Course
  currentGroup?: VideoGroup
  isExpanded: boolean
  onChangeIsExpanded: (value: boolean) => void
}

const View = ({
  id,
  course,
  currentGroup,
  isExpanded,
  onChangeIsExpanded,
}: Props) => {
  const [height, setHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [hoverStates, setHoverStates] = useState<boolean[]>([])
  const [hoverStatesPA, setHoverStatesPA] = useState<boolean[]>([])

  const access = useSelector(selectMemberAccess)

  const software = course?.tags?.find(
    item => item.category === 'SOFTWARE_COURSE',
  )

  const isSoftware = software ? true : false

  const practicalApplication = course?.tags?.find(
    item => item.category === 'PRACTICAL_APPLICATION_COURSE',
  )

  const isPracticalApplication = practicalApplication ? true : false

  const practicalApplicationsLessons = currentGroup?.lessons.filter(
    item => item.isPracticalApplication,
  )
  const regularLessons = currentGroup?.lessons.filter(
    item => !item.isPracticalApplication,
  )

  const hasPAAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.practical_apps?.value) === 1
      : false

  const handleMouseEnter = (index: number, section?: string) => {
    if (section === 'PA') {
      setHoverStatesPA(
        prev => prev?.map((hover, i) => (i === index ? true : hover)),
      )
    } else {
      setHoverStates(
        prev => prev?.map((hover, i) => (i === index ? true : hover)),
      )
    }
  }

  const handleMouseLeave = (index: number, section?: string) => {
    if (section === 'PA') {
      setHoverStatesPA(
        prev => prev?.map((hover, i) => (i === index ? false : hover)),
      )
    } else {
      setHoverStates(
        prev => prev?.map((hover, i) => (i === index ? false : hover)),
      )
    }
  }

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
    }
  }, [currentGroup])

  useEffect(() => {
    if (regularLessons && regularLessons.length !== hoverStates.length) {
      setHoverStates(Array(regularLessons.length).fill(false))
    }

    if (
      practicalApplicationsLessons &&
      practicalApplicationsLessons.length !== hoverStatesPA.length
    ) {
      setHoverStatesPA(Array(practicalApplicationsLessons.length).fill(false))
    }
  }, [regularLessons, practicalApplicationsLessons])

  const getIcon = (hover: boolean, isActive: boolean, lesson: Lesson) => {
    if (lesson.isComplete || lesson.percentage) {
      return <StyledIcon src={CompleteIcon} alt="Completed" />
    }

    if (lesson.assetTypeAbbr === 'video' && (isActive || hover)) {
      return <StyledIcon src={VideoIconActive} alt="Active Video" />
    }

    if (lesson.assetTypeAbbr === 'quiz' && (isActive || hover)) {
      return <StyledIcon src={QuizIconActive} alt="Active Quiz" />
    }

    if (lesson.assetTypeAbbr === 'video') {
      return <StyledIcon src={VideoIcon} alt="Video" />
    }

    if (lesson.assetTypeAbbr === 'quiz') {
      return <StyledIcon src={QuizIcon} alt="Quiz" />
    }
  }

  return (
    <SidenavBox $isExpanded={isExpanded} height={height} ref={contentRef}>
      <SidenavToggleBox
        onClick={() => onChangeIsExpanded(!isExpanded)}
        $isExpanded={isExpanded}
      >
        <img
          src={isExpanded ? SidenavHideIcon : SidenavExpandIcon}
          alt="Toggle Menu"
        />
        {isExpanded && <span>Hide menu</span>}
      </SidenavToggleBox>
      <SidenavExpandedContent>
        <HeaderTitle>
          {isSoftware ? (
            <>{course?.title}</>
          ) : (
            <>
              {isPracticalApplication ? (
                <>Practical Applications</>
              ) : (
                <>{currentGroup?.title}</>
              )}
            </>
          )}
        </HeaderTitle>
        <Divider />
        <ObjectiveTitle>Currently Viewing:</ObjectiveTitle>
        <ObjectiveText>
          <span>{currentGroup?.currentlyViewing}</span>
        </ObjectiveText>
        <DayItems>
          {regularLessons?.map((lesson, key) => {
            const isActive = id === lesson.id

            return (
              <React.Fragment key={`replay-item-${key}`}>
                <Divider />
                <LinkBox
                  component={RouterLink}
                  to={`/courses/${currentGroup?.courseId}/${
                    lesson.assetTypeAbbr
                  }/${lesson.id}${lesson.isFree ? '/free' : ''}`}
                >
                  <DayItem
                    $isActive={isActive}
                    onMouseEnter={() => handleMouseEnter(key)}
                    onMouseLeave={() => handleMouseLeave(key)}
                  >
                    {getIcon(hoverStates[key], isActive, lesson)}
                    <div>
                      <DayItemText $isLarge={true}>
                        {lesson.shortDescription}
                      </DayItemText>
                      <DayItemText>
                        {lesson.assetTypeAbbr === 'video' && (
                          <>{lesson.videoDuration}</>
                        )}
                        {lesson.assetTypeAbbr === 'quiz' &&
                          lesson.questions && (
                            <>{pluralize('Question', lesson.questions, true)}</>
                          )}
                      </DayItemText>
                    </div>
                  </DayItem>
                </LinkBox>
              </React.Fragment>
            )
          })}
        </DayItems>
        {practicalApplicationsLessons &&
          practicalApplicationsLessons.length > 0 && (
            <>
              <Divider />
              <ObjectiveTitle>Practical Applications</ObjectiveTitle>
              <ObjectiveText>
                {!hasPAAccess && (
                  <>
                    <span>—</span>
                    <Link to={'/profile/settings/manage'}>UPGRADE TO PRO</Link>
                  </>
                )}
              </ObjectiveText>
              <DayItems>
                {practicalApplicationsLessons?.map((lesson, key) => {
                  const isActive = id === lesson.id

                  return (
                    <React.Fragment key={`replay-item-${key}`}>
                      <Divider />
                      <LinkBox
                        component={RouterLink}
                        to={`/courses/${currentGroup?.courseId}/${
                          lesson.assetTypeAbbr
                        }/${lesson.id}${lesson.isFree ? '/free' : ''}`}
                      >
                        <DayItem
                          $isActive={isActive}
                          onMouseEnter={() => handleMouseEnter(key, 'PA')}
                          onMouseLeave={() => handleMouseLeave(key, 'PA')}
                        >
                          {getIcon(hoverStatesPA[key], isActive, lesson)}
                          <div>
                            <DayItemText $isLarge={true}>
                              {lesson.shortDescription}
                            </DayItemText>
                            <DayItemText>{lesson.videoDuration}</DayItemText>
                          </div>
                        </DayItem>
                      </LinkBox>
                    </React.Fragment>
                  )
                })}
              </DayItems>
            </>
          )}
      </SidenavExpandedContent>
    </SidenavBox>
  )
}

export default View
